import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Sparkles } from "lucide-react";

// Feb-2026 · NB Token Phase 2/3 · Full-screen celebration modal that fires once when
// a first-deposit NBT allocation lands. Auto-acknowledges on close.
export default function NBFirstDepositModal() {
  const [tx, setTx] = useState(null);
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/nb-token/pending-celebration");
        if (mounted && data?.pending && data?.transaction) {
          setTx(data.transaction);
          try {
            const w = await api.get("/nb-token/wallet");
            if (mounted) setWallet(w.data);
          } catch {
            /* ignore */
          }
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const close = async () => {
    if (!tx) return;
    try {
      await api.post(`/nb-token/celebration-ack/${tx.id}`);
    } catch {
      /* ignore */
    }
    setTx(null);
  };

  if (!tx) return null;
  const symbol = wallet?.symbol || "NBT";
  const logo = wallet?.logo_url || "/icons/nb-token-logo.jpg";
  const amount = Number(tx.amount_nb || 0);
  const value = Number(tx.value_usdt || 0);
  const price = Number(tx.price_usdt || 0);

  return (
    <div
      data-testid="nb-first-deposit-modal"
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        className="nb-card p-6 md:p-8 w-full max-w-md relative overflow-hidden"
        style={{
          background:
            "radial-gradient(circle at top, rgba(234,179,8,0.25) 0%, rgba(15,22,38,1) 60%)",
          borderColor: "rgba(234,179,8,0.5)",
        }}
      >
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#EAB308]/25 blur-3xl" />
        <div className="absolute -left-10 -bottom-10 h-40 w-40 rounded-full bg-[#8AE234]/15 blur-3xl" />
        <button
          data-testid="nb-first-deposit-close"
          onClick={close}
          className="absolute top-3 right-3 text-slate-400 hover:text-white"
        >
          <X size={18} />
        </button>
        <div className="flex flex-col items-center text-center relative">
          <div className="relative">
            <img
              src={logo}
              alt="NBT"
              className="h-24 w-24 rounded-2xl ring-4 ring-[#EAB308]/40"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
            <Sparkles
              size={28}
              className="absolute -top-2 -right-2 text-[#EAB308] animate-pulse"
            />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#EAB308] mt-4">
            First Deposit Reward
          </div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mt-2">
            🎉 You earned <span className="text-[#EAB308]">{amount.toFixed(4)}</span> {symbol}!
          </h2>
          <p className="text-slate-400 text-sm mt-3 max-w-sm">
            Your first-deposit NBT allocation just landed. Value at issue:{" "}
            <span className="text-[#8AE234] font-mono">${value.toFixed(2)}</span> USDT @ ${price.toFixed(4)}.
          </p>
          <div className="mt-5 w-full grid grid-cols-3 gap-2 text-xs">
            <div className="bg-black/40 border border-white/10 rounded-lg p-3">
              <div className="text-slate-500 uppercase tracking-widest text-[10px]">NBT</div>
              <div className="font-mono text-[#EAB308] mt-1">{amount.toFixed(4)}</div>
            </div>
            <div className="bg-black/40 border border-white/10 rounded-lg p-3">
              <div className="text-slate-500 uppercase tracking-widest text-[10px]">Value</div>
              <div className="font-mono text-[#8AE234] mt-1">${value.toFixed(2)}</div>
            </div>
            <div className="bg-black/40 border border-white/10 rounded-lg p-3">
              <div className="text-slate-500 uppercase tracking-widest text-[10px]">Lock</div>
              <div className="font-mono text-white mt-1">18 mo</div>
            </div>
          </div>
          <div className="mt-5 flex gap-2 w-full">
            <button
              data-testid="nb-first-deposit-view"
              onClick={async () => {
                await close();
                window.location.href = "/nb-token";
              }}
              className="flex-1 nb-btn-primary text-sm"
            >
              View my NBT wallet
            </button>
            <button
              data-testid="nb-first-deposit-dismiss"
              onClick={close}
              className="flex-1 border border-white/10 rounded-lg py-2 text-sm text-slate-300 hover:bg-white/5"
            >
              Awesome
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
